import { useRef, useState } from 'react'
import { data, Link, useLoaderData, type LoaderFunctionArgs, type MetaFunction } from 'react-router'
import { requestOptions, type RequestOptions } from '@/lib/http'
import missingImg from '@/site/assets/launcher-icon-4x.png'
import SectionHeading from '@/site/components/SectionHeading'
import SupplyList from '@/site/components/SupplyList'
import TutorialCard from '@/site/components/TutorialCard'
import TutorialEnrichment from '@/site/components/TutorialEnrichment'
import TutorialProducts from '@/site/components/TutorialProducts'
import VideoPlayer from '@/site/components/VideoPlayer'
import VideoTranscript from '@/site/components/VideoTranscript'
import { fetchTutorialBySlug } from '@/site/lib/api'
import { chaptersOf, faqOf, secondsFromTimestamp, seoDescriptionOf, summaryOf } from '@/site/lib/enrichment'
import { facetHref, splitFacetValue } from '@/site/lib/format'
import { LIVE_UNPUBLISHED_TUTORIAL_IDS } from '@/site/lib/liveUnpublished'
import { searchTutorials } from '@/site/lib/search'
import { absoluteUrl, breadcrumbLd, describe, errorMeta, isoDuration, seoMeta, SITE_NAME, siteUrlOf } from '@/site/lib/seo'
import { useSiteData } from '@/site/lib/siteData'
import {
  aboutSnippet,
  categoryOf,
  cleanTitle,
  crumbTitle,
  difficultyOf,
  displayTitle,
  facetValueOf,
  heroImageOf,
  instructorOf,
  RELATED_FACETS,
  transcriptHtml,
} from '@/site/lib/tutorial'
import type { Tutorial as TutorialData, TutorialHit } from '@/site/lib/types'

/**
 * /tutorial/:slug. Unknown slugs and unpublished tutorials are a 404, except the unpublished ones Google still sends
 * traffic to; API failures are errors (never a 404 search engines would act on).
 */
export async function loader({ request, params }: LoaderFunctionArgs) {
  const opts = requestOptions(request)
  const tutorial = await fetchTutorialBySlug(params.slug ?? '', opts)
  if (!tutorial || (!tutorial.active_at && !LIVE_UNPUBLISHED_TUTORIAL_IDS.has(tutorial.id))) throw data(null, { status: 404 })
  return { tutorial, related: await loadRelated(tutorial, opts) }
}

export interface Related {
  /** e.g. { facet: "Fabric Cut", value: "Layer Cake - 10\" Squares" } */
  facet: string
  value: string
  hits: TutorialHit[]
}

/** Popular tutorials sharing the first of RELATED_FACETS this tutorial has. Optional: failures give none. */
async function loadRelated(tutorial: TutorialData, opts: RequestOptions): Promise<Related | null> {
  for (const facet of RELATED_FACETS) {
    const category = categoryOf(tutorial, facet)
    if (!category) continue
    const value = facetValueOf(category.title)
    try {
      const res = await searchTutorials({ filters: { [facet]: [value] }, sort: 'popular', perPage: 5 }, opts)
      const hits = (res.data ?? []).filter((h) => String(h.id) !== String(tutorial.id) && h.slug !== tutorial.slug).slice(0, 4)
      return hits.length ? { facet, value, hits } : null
    } catch {
      return null
    }
  }
  return null
}

/**
 * A tutorial scheduled for a future date: viewable by link (a preview) but kept out of search engines.
 *
 * Tutorials without a publish date are a 404 (the loader), except the ones Google still sends traffic to (e.g.
 * /tutorial/donnas-new-very-easy-free-mosaic-quilt-pattern): those stay indexed by link, out of the listings, search
 * and the sitemap, as in v1.
 */
export function isScheduled(tutorial: Pick<TutorialData, 'active_at'>, now: number = Date.now()): boolean {
  const at = tutorial.active_at ? Date.parse(tutorial.active_at) : NaN
  return Number.isFinite(at) && at > now
}

export const meta: MetaFunction<typeof loader> = ({ loaderData, matches, error }) => {
  if (!loaderData || error) return errorMeta(404)

  const { tutorial } = loaderData
  const siteUrl = siteUrlOf(matches)
  const path = `/tutorial/${tutorial.slug}`
  // v1 PR #5: the generated SEO description, then the summary, then the about / search snippet.
  const description = describe(seoDescriptionOf(tutorial) || summaryOf(tutorial) || aboutSnippet(tutorial) || tutorial.snippets?.find((s) => s.type === 'search')?.content)
  const image = heroImageOf(tutorial)
  const youtubeId = tutorial.video?.youtube_id
  const instructor = instructorOf(tutorial)
  const thumbnails = [image, tutorial.images?.find((i) => i.type === 'youtube_maxres')?.url].filter(
    (url, i, all): url is string => !!url && all.indexOf(url) === i,
  )

  // Chapters as schema.org Clips (each ends where the next starts; the last at the video's end).
  const chapters = chaptersOf(tutorial)
  const videoSeconds = secondsFromTimestamp(tutorial.video?.duration)
  const clips = chapters.flatMap((chapter, i) => {
    const endOffset = chapters[i + 1]?.startOffset ?? videoSeconds
    return endOffset > chapter.startOffset
      ? [{ '@type': 'Clip', name: chapter.name, startOffset: chapter.startOffset, endOffset, url: `https://www.youtube.com/watch?v=${youtubeId}&t=${chapter.startOffset}s` }]
      : []
  })
  const faq = faqOf(tutorial)

  const video = youtubeId
    ? {
        '@context': 'https://schema.org',
        '@type': 'VideoObject',
        name: cleanTitle(tutorial.title),
        description,
        ...(thumbnails.length ? { thumbnailUrl: thumbnails } : {}),
        // Required by Google; undated (unlisted) tutorials fall back to when they were created.
        ...(tutorial.active_at || tutorial.created_at ? { uploadDate: tutorial.active_at || tutorial.created_at } : {}),
        ...(tutorial.updated_at ? { dateModified: tutorial.updated_at } : {}),
        ...(isoDuration(tutorial.video?.duration) ? { duration: isoDuration(tutorial.video?.duration) } : {}),
        embedUrl: `https://www.youtube.com/embed/${youtubeId}`,
        url: absoluteUrl(siteUrl, path),
        ...(instructor ? { actor: { '@type': 'Person', name: instructor.title } } : {}),
        publisher: { '@type': 'Organization', name: SITE_NAME, logo: { '@type': 'ImageObject', url: absoluteUrl(siteUrl, '/launcher-icon-4x.png') } },
        ...(clips.length ? { hasPart: clips } : {}),
      }
    : null

  return seoMeta({
    title: `${cleanTitle(tutorial.title)} - ${SITE_NAME}`,
    description,
    siteUrl,
    path,
    image,
    type: youtubeId ? 'video.other' : 'website',
    robots: isScheduled(tutorial) ? 'noindex' : undefined,
    jsonLd: [
      ...(video ? [video] : []),
      breadcrumbLd(siteUrl, [
        { name: SITE_NAME, path: '/' },
        { name: 'Tutorials', path: '/tutorials' },
        { name: crumbTitle(tutorial.title).trim(), path },
      ]),
      ...(faq.length
        ? [
            {
              '@context': 'https://schema.org',
              '@type': 'FAQPage',
              mainEntity: faq.map((item) => ({ '@type': 'Question', name: item.question, acceptedAnswer: { '@type': 'Answer', text: item.answer } })),
            },
          ]
        : []),
    ],
  })
}

export default function Tutorial() {
  const { tutorial, related } = useLoaderData<typeof loader>()
  const { recent, collections } = useSiteData()
  const instructor = instructorOf(tutorial)
  const about = aboutSnippet(tutorial)
  const transcript = transcriptHtml(tutorial)
  const title = displayTitle(tutorial.title)
  const shopUrl = tutorial.links?.[0]?.url
  const pdfUrl = tutorial.pdfs?.[0]?.url
  const hasSize = (tutorial.quilt_size_width ?? 0) > 0 && (tutorial.quilt_size_length ?? 0) > 0
  const [seek, setSeek] = useState<{ seconds: number; at: number } | null>(null)
  const playerRef = useRef<HTMLDivElement>(null)
  const playFrom = (seconds: number) => {
    setSeek({ seconds, at: Date.now() })
    playerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }

  const chips = [
    ...(['Fabric Cut', 'Theme', 'Project Type'] as const).flatMap((facet) => {
      const category = categoryOf(tutorial, facet)
      if (!category) return []
      const value = facetValueOf(category.title)
      return [{ label: splitFacetValue(facet, value).name, to: facetHref(facet, value, collections) }]
    }),
    ...(difficultyOf(tutorial) ? [{ label: difficultyOf(tutorial)!, to: facetHref('Skill Level', difficultyOf(tutorial)!, collections) }] : []),
  ]
  const moreHits = related?.hits ?? recent.filter((h) => h.slug !== tutorial.slug).slice(0, 4)
  const moreTitle = related ? `More ${splitFacetValue(related.facet, related.value).name} tutorials` : 'Recent tutorials'
  const moreLink = related ? facetHref(related.facet, related.value, collections) : '/tutorials'

  return (
    <div className="qt-container mt-5 md:mt-8">
      <nav aria-label="Breadcrumb" className="mb-4 text-[13px] font-semibold text-qt-muted">
        <ol className="flex flex-wrap gap-1.5">
          <li>
            <Link to="/" className="hover:underline">Home</Link> <span aria-hidden="true">/</span>
          </li>
          <li>
            <Link to="/tutorials" className="hover:underline">Tutorials</Link> <span aria-hidden="true">/</span>
          </li>
          <li aria-current="page" className="truncate">{crumbTitle(tutorial.title).trim()}</li>
        </ol>
      </nav>

      <div className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_360px] lg:gap-12">
        <div className="flex min-w-0 flex-col gap-5">
          <div ref={playerRef}>
            <VideoPlayer youtubeId={tutorial.video?.youtube_id} image={heroImageOf(tutorial)} title={title} duration={tutorial.video?.duration} seek={seek} />
          </div>

          <h1 className="text-3xl leading-tight font-extrabold text-qt-ink md:text-[44px]">{title}</h1>

          <ul className="flex flex-wrap gap-2" aria-label="About this tutorial">
            {instructor && (
              <li>
                <Link to={facetHref('Instructors', instructor.title, collections)} className="flex items-center gap-2 rounded-xl bg-qt-soft py-1 pr-3 pl-1 text-sm font-bold hover:underline">
                  <img src={instructor.images?.[0]?.url || missingImg} alt="" width="28" height="28" className="size-7 rounded-lg object-cover" />
                  {instructor.title}
                </Link>
              </li>
            )}
            {chips.map((chip) => (
              <li key={chip.label}>
                <Link to={chip.to} className="block rounded-xl border-2 border-qt-line px-3 py-1.5 text-sm font-bold hover:border-qt-stitch">
                  {chip.label}
                </Link>
              </li>
            ))}
            {hasSize && (
              <li className="rounded-xl border-2 border-qt-line px-3 py-1.5 text-sm font-bold">
                Quilt size {tutorial.quilt_size_width}" × {tutorial.quilt_size_length}"
              </li>
            )}
          </ul>

          {about && <p className="text-base leading-7 md:text-[17px]">{about}</p>}

          {(shopUrl || pdfUrl) && (
            <div className="flex flex-wrap gap-3">
              {shopUrl && (
                <a href={shopUrl} target="_blank" rel="noreferrer" className="rounded-2xl bg-qt-primary px-6 py-3.5 text-base font-bold text-white hover:bg-qt-primary-hover">
                  {tutorial.links[0].title || 'Get the supplies'}
                </a>
              )}
              {pdfUrl && (
                <a href={pdfUrl} target="_blank" rel="noreferrer" download className="rounded-2xl border-2 border-qt-text px-6 py-3 text-base font-bold hover:bg-qt-soft">
                  Download the pattern PDF
                </a>
              )}
            </div>
          )}

          {instructor && (
            <section aria-label="Instructor" className="mt-2 flex items-start gap-4 rounded-3xl bg-qt-soft p-5">
              <img
                loading="lazy"
                src={instructor.images?.[0]?.url || missingImg}
                alt=""
                width="80"
                height="80"
                className="size-20 shrink-0 rounded-2xl object-cover"
                onError={(e) => {
                  e.currentTarget.src = missingImg
                }}
              />
              <div className="flex flex-col gap-1">
                <p className="text-[13px] font-bold tracking-wide text-qt-muted uppercase">Instructor</p>
                <p className="text-lg font-extrabold">{instructor.title}</p>
                {instructor.description && <p className="text-sm leading-6">{instructor.description}</p>}
              </div>
            </section>
          )}
        </div>

        <aside className="lg:sticky lg:top-6 lg:self-start">
          <SupplyList list={tutorial.supplies ?? []} shopUrl={shopUrl} />
        </aside>
      </div>

      {tutorial.products?.length > 0 && (
        <div className="mt-14">
          <TutorialProducts products={tutorial.products} />
        </div>
      )}

      <div className="mt-14 empty:hidden">
        <TutorialEnrichment tutorial={tutorial} onSeek={playFrom} />
      </div>

      {transcript.length > 0 && (
        <div className="mt-14">
          <VideoTranscript snippet={transcript} />
        </div>
      )}

      {moreHits.length > 0 && (
        <section aria-labelledby="more" className="mt-14 flex flex-col gap-6">
          <SectionHeading id="more" more={{ to: moreLink }}>
            {moreTitle}
          </SectionHeading>
          <div className="grid grid-cols-2 gap-4 md:gap-6 lg:grid-cols-4">
            {moreHits.map((hit, i) => (
              <TutorialCard key={hit.id ?? i} hit={hit} />
            ))}
          </div>
        </section>
      )}
    </div>
  )
}
